import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import debounce from 'lodash.debounce';
import { useAuth } from '../contexts/AuthContext';
import './StickyNote.css';

const DRAWING_COLORS = [
  '#2c2c2c', // Dark gray (pen)
  '#1a5f7a', // Blue
  '#c73659', // Red/pink
  '#5a8f3d', // Green
  '#8b5a2b', // Brown
];

// Number of days before a note can be pinned to the wall
const DAYS_BEFORE_PINNABLE = 10;

const StickyNote = ({ id, note, onUpdate, onDelete, onPin }) => {
  const { userProfile, currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState(DRAWING_COLORS[0]);
  const [brushSize] = useState(2);
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [text, setText] = useState(note.text || '');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [localPosition, setLocalPosition] = useState(null); // Track local position after drag
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const textareaRef = useRef(null);
  const noteRef = useRef(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });

  // Check if note has text content (non-empty)
  const hasText = note.text && note.text.trim().length > 0;
  // Check if note has a drawing
  const hasDrawing = !!note.drawing;
  // Check if note has any content (text or drawing)
  const hasContent = hasText || hasDrawing;

  // Check if current user is the owner
  const isOwner = note.createdByEmail === currentUser?.email ||
                  note.createdBy === userProfile?.name;

  // Check if note is old enough to be pinned (10 days since creation, no edits in 10 days)
  const canBePinned = useMemo(() => {
    if (!note.createdAt || !hasContent) return false;

    const createdDate = new Date(note.createdAt);
    const lastEditDate = note.lastEditedAt ? new Date(note.lastEditedAt) : createdDate;
    const now = new Date();

    const daysSinceCreation = (now - createdDate) / (1000 * 60 * 60 * 24);
    const daysSinceLastEdit = (now - lastEditDate) / (1000 * 60 * 60 * 24);

    return daysSinceCreation >= DAYS_BEFORE_PINNABLE && daysSinceLastEdit >= DAYS_BEFORE_PINNABLE;
  }, [note.createdAt, note.lastEditedAt, hasContent]);

  console.log('Owner check:', {
    noteEmail: note.createdByEmail,
    noteCreator: note.createdBy,
    currentEmail: currentUser?.email,
    profileName: userProfile?.name,
    isOwner
  });

  // Debounced Firebase update - only fires 500ms after drag stops
  const debouncedFirebaseUpdate = useMemo(
    () => debounce((position) => {
      console.log('Saving position to Firebase:', position);
      onUpdate({ position });
    }, 500),
    [onUpdate]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFirebaseUpdate.cancel();
    };
  }, [debouncedFirebaseUpdate]);

  // Sync FROM Firebase when not dragging and no local override
  useEffect(() => {
    if (!isDragging && !localPosition && note.position) {
      console.log('Syncing from Firebase:', note.position);
    }
  }, [note.position, isDragging, localPosition]);

  // Sync text from Firebase
  useEffect(() => {
    setText(note.text || '');
  }, [note.text]);

  // Initialize canvas when entering draw mode
  useEffect(() => {
    if (isDrawMode && canvasRef.current) {
      const canvas = canvasRef.current;
      // Set canvas size based on the sticky note dimensions
      const noteWidth = 180 - 16; // note width minus padding
      const noteHeight = 180 - 35; // note height minus padding and meta area
      canvas.width = noteWidth;
      canvas.height = noteHeight;

      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.strokeStyle = drawColor;
      context.lineWidth = brushSize;
      contextRef.current = context;

      // Load existing drawing if available
      if (note.drawing) {
        const img = new Image();
        img.onload = () => {
          context.drawImage(img, 0, 0);
        };
        img.src = note.drawing;
      }
    }
  }, [isDrawMode, note.drawing]);

  // Update context when color/size changes
  useEffect(() => {
    if (contextRef.current) {
      contextRef.current.strokeStyle = drawColor;
      contextRef.current.lineWidth = brushSize;
    }
  }, [drawColor, brushSize]);

  // Debounced save for drawing data
  const debouncedDrawingSave = useMemo(
    () => debounce((drawingData) => {
      console.log('Saving drawing to Firebase');
      onUpdate({ drawing: drawingData, lastEditedAt: new Date().toISOString() });
    }, 1000),
    [onUpdate]
  );

  // Cleanup drawing debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDrawingSave.cancel();
    };
  }, [debouncedDrawingSave]);

  const saveDrawing = useCallback(() => {
    if (canvasRef.current) {
      const drawingData = canvasRef.current.toDataURL('image/png');
      debouncedDrawingSave(drawingData);
    }
  }, [debouncedDrawingSave]);

  const startDrawing = ({ nativeEvent }) => {
    if (!isDrawMode || !contextRef.current) return;
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);

    // Save state for undo
    if (canvasRef.current) {
      setDrawingHistory(prev => [...prev, canvasRef.current.toDataURL()]);
    }
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !contextRef.current) return;
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    contextRef.current?.closePath();
    setIsDrawing(false);
    saveDrawing();
  };

  const handleUndo = () => {
    if (drawingHistory.length === 0 || !canvasRef.current || !contextRef.current) return;

    const newHistory = [...drawingHistory];
    const previousState = newHistory.pop();
    setDrawingHistory(newHistory);

    const img = new Image();
    img.onload = () => {
      contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      contextRef.current.drawImage(img, 0, 0);
      saveDrawing();
    };
    img.src = previousState;
  };

  const handleClearDrawing = () => {
    if (!canvasRef.current || !contextRef.current) return;
    // Save current state for undo
    setDrawingHistory(prev => [...prev, canvasRef.current.toDataURL()]);
    contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    saveDrawing();
  };

  const toggleDrawMode = () => {
    if (isDrawMode) {
      // Exiting draw mode - save final state
      saveDrawing();
    }
    setIsDrawMode(!isDrawMode);
    setIsEditing(false);
  };

  // Auto-resize and focus textarea when editing
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (text !== note.text) {
      onUpdate({ text, lastEditedAt: new Date().toISOString() });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      textareaRef.current?.blur();
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (!isOwner || isEditing || isDrawMode) return;

    // Don't start drag if clicking on delete button or draw controls
    if (e.target.closest('.sticky-note-delete')) return;
    if (e.target.closest('.sticky-note-draw-toggle')) return;
    if (e.target.closest('.sticky-note-draw-controls')) return;
    if (e.target.closest('.sticky-note-canvas')) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    console.log('Mouse down at:', e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;

    setDragOffset({ x: deltaX, y: deltaY });
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;

    console.log('Mouse up');
    setIsDragging(false);

    // Calculate new position
    const currentPos = note.position || { x: 100, y: 100 };
    const newPosition = {
      x: currentPos.x + dragOffset.x,
      y: currentPos.y + dragOffset.y
    };

    console.log('Final position:', newPosition);

    // Set local position immediately (prevents jump)
    setLocalPosition(newPosition);

    // Reset drag offset
    setDragOffset({ x: 0, y: 0 });

    // Save to Firebase
    debouncedFirebaseUpdate(newPosition);

    // Clear local position after Firebase updates (give it time to sync)
    setTimeout(() => {
      setLocalPosition(null);
    }, 1000);
  };

  // Add/remove mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, note.position]);

  // Get position: use local override if available, otherwise Firebase
  const basePosition = localPosition || note.position || { x: 100, y: 100 };

  // Calculate final position (base position + current drag offset)
  const finalPosition = {
    x: basePosition.x + dragOffset.x,
    y: basePosition.y + dragOffset.y
  };

  console.log('Rendering note at:', finalPosition, 'isDragging:', isDragging, 'localPos:', localPosition);

  return (
    <motion.div
      ref={noteRef}
      className={`sticky-note ${!isOwner ? 'not-owner' : ''} ${isDragging ? 'dragging' : ''} ${note.drawing ? 'has-drawing' : ''}`}
      style={{
        backgroundColor: note.color || 'var(--color-watercolor-yellow)',
        position: 'absolute',
        left: `${finalPosition.x}px`,
        top: `${finalPosition.y}px`,
        cursor: isOwner ? (isDragging ? 'grabbing' : 'grab') : 'default',
        // Smooth transition when position changes from Firebase (other user moving)
        // But disable during active drag for immediate feedback
        transition: isDragging ? 'none' : 'left 0.8s ease-out, top 0.8s ease-out',
      }}
      onMouseDown={handleMouseDown}
      initial={{ scale: 0, opacity: 0, rotate: -10 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      exit={{ scale: 0, opacity: 0, rotate: 20 }}
      transition={{ type: 'spring', stiffness: 500, damping: 50 }}
    >
      {/* Delete button - only for owner */}
      {isOwner && (
        <button
          className="sticky-note-delete"
          onClick={onDelete}
          aria-label="Delete note"
          title="Delete note"
        >
          ×
        </button>
      )}

      {/* Pin to wall button - only for owner, only if note is old enough and has content */}
      {isOwner && canBePinned && onPin && (
        <button
          className="sticky-note-pin"
          onClick={() => setShowPinPrompt(true)}
          aria-label="Pin to wall"
          title="Pin this note to the wall"
        >
          📌
        </button>
      )}

      {/* Pin confirmation prompt */}
      {showPinPrompt && (
        <div className="sticky-note-pin-prompt">
          <p>Pin this note to the wall?</p>
          <p className="pin-prompt-subtitle">It will become a permanent decoration</p>
          <div className="pin-prompt-buttons">
            <button
              className="pin-prompt-btn pin-prompt-confirm"
              onClick={() => {
                onPin(id, note);
                setShowPinPrompt(false);
              }}
            >
              Pin it!
            </button>
            <button
              className="pin-prompt-btn pin-prompt-cancel"
              onClick={() => setShowPinPrompt(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Draw mode toggle - only for owner, and only if no text exists */}
      {isOwner && !hasText && (
        <button
          className={`sticky-note-draw-toggle ${isDrawMode ? 'active' : ''}`}
          onClick={toggleDrawMode}
          aria-label={isDrawMode ? 'Exit draw mode' : 'Enter draw mode'}
          title={isDrawMode ? 'Exit draw mode' : 'Draw on note'}
        >
          ✏️
        </button>
      )}

      {/* Drawing controls - visible in draw mode */}
      {isDrawMode && isOwner && (
        <div className="sticky-note-draw-controls">
          <div className="draw-colors">
            {DRAWING_COLORS.map((color) => (
              <button
                key={color}
                className={`color-btn ${drawColor === color ? 'active' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setDrawColor(color)}
                title={`Color: ${color}`}
              />
            ))}
          </div>
          <div className="draw-tools">
            <button
              className="tool-btn"
              onClick={handleUndo}
              title="Undo"
              disabled={drawingHistory.length === 0}
            >
              ↩️
            </button>
            <button
              className="tool-btn"
              onClick={handleClearDrawing}
              title="Clear drawing"
            >
              🗑️
            </button>
          </div>
        </div>
      )}

      {/* Canvas for drawing */}
      {isDrawMode && (
        <canvas
          ref={canvasRef}
          className="sticky-note-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      )}

      {/* Display saved drawing when not in draw mode */}
      {!isDrawMode && note.drawing && (
        <img
          src={note.drawing}
          alt="Drawing"
          className="sticky-note-drawing-display"
        />
      )}

      {/* Note content - only show if no drawing exists */}
      {!hasDrawing && !isDrawMode && (
        <div
          className="sticky-note-content"
          onClick={() => isOwner && !isEditing && setIsEditing(true)}
        >
          {isEditing ? (
            <textarea
              ref={textareaRef}
              className="sticky-note-textarea"
              value={text}
              onChange={handleTextChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder="Write a note..."
              rows={3}
            />
          ) : (
            <p className="sticky-note-text">
              {text || 'Click to write...'}
            </p>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="sticky-note-meta">
        <span className="sticky-note-author">
          {note.createdBy || 'Unknown'}
        </span>
      </div>

      {/* Paper texture overlay */}
      <div className="sticky-note-texture" />
    </motion.div>
  );
};

export default StickyNote;
